import { useEffect, useMemo, useState } from 'react';
import { Box, Card, Grid, Stack, Typography, Checkbox, Divider, IconButton } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import StickyNote2Rounded from '@mui/icons-material/StickyNote2Rounded';
import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import ShoppingCartRounded from '@mui/icons-material/ShoppingCartRounded';
import FolderRounded from '@mui/icons-material/FolderRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import CalendarMonthRounded from '@mui/icons-material/CalendarMonthRounded';
import BarChartRounded from '@mui/icons-material/BarChartRounded';
import ChatRounded from '@mui/icons-material/ChatRounded';
import LibraryMusicRounded from '@mui/icons-material/LibraryMusicRounded';
import OndemandVideoRounded from '@mui/icons-material/OndemandVideoRounded';
import WorkRounded from '@mui/icons-material/WorkRounded';
import SpeedRounded from '@mui/icons-material/SpeedRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import {
  fetchNotes, listPayments, getPaymentStats, getGroceryStats, getDocumentStats,
  getReportsOverview, PaymentItem,
} from '../services/api';
import { getStoredUser } from '../utils/auth';
import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/ui/StatCard';
import DonutChart from '../components/ui/charts/DonutChart';
import { CATEGORICAL } from '../components/ui/charts/chartTokens';
import { useThemeMode } from '../theme/ThemeModeContext';

const QUOTES = [
  'A small note today, a stress-free tomorrow.',
  'Small steps, done daily, beat big plans done never.',
  'Clarity comes from writing things down.',
  'Progress, not perfection.',
];

type AgendaItem = { title: string; date: Date; kind: 'note' | 'payment' };

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/* ------------------------------------------------------------------ */
/*  Visual building blocks                                             */
/* ------------------------------------------------------------------ */
const float1 = keyframes`0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(-30px,24px) scale(1.12)}`;
const float2 = keyframes`0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(36px,-20px) scale(.92)}`;
const sheen = keyframes`0%{transform:translateX(-140%) skewX(-22deg)}22%,100%{transform:translateX(420%) skewX(-22deg)}`;
const bob = keyframes`0%,100%{transform:translateY(0) rotate(-12deg)}50%{transform:translateY(-6px) rotate(-8deg)}`;

const fmtMoney = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`;

/** Animates a number from 0 to `value` whenever it changes. */
const useCountUp = (value: number, ms = 900) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setV(value);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / ms);
      setV(value * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, ms]);
  return v;
};

const glassCardSx = {
  borderRadius: '22px',
  position: 'relative',
  overflow: 'hidden',
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 10px 30px rgba(98,38,167,0.08)',
  '&::before': {
    content: '""', position: 'absolute', left: 0, right: 0, top: 0, height: 4,
    background: 'linear-gradient(90deg,#6226A7,#EC4899,#F6A938)',
  },
} as const;

const HeroStat = ({ icon: Icon, label, value }: { icon: React.ComponentType<any>; label: string; value: string }) => (
  <Box
    sx={{
      flex: '1 1 150px', minWidth: 0, px: 2, py: 1.5, borderRadius: '18px', color: '#fff',
      background: 'linear-gradient(135deg, rgba(255,255,255,.30), rgba(255,255,255,.08))',
      border: '1px solid rgba(255,255,255,.38)',
      backdropFilter: 'blur(10px)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,.5), 0 8px 20px rgba(0,0,0,.12)',
    }}
  >
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ opacity: 0.9 }}>
      <Icon sx={{ fontSize: 16 }} />
      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4 }}>{label}</Typography>
    </Stack>
    <Typography sx={{ fontWeight: 900, fontSize: { xs: 22, md: 26 }, lineHeight: 1.2 }} noWrap>{value}</Typography>
  </Box>
);

type TileProps = {
  title: string;
  metric: string;
  hint?: string;
  icon: React.ComponentType<any>;
  colors: [string, string];
  onClick: () => void;
  span?: { xs: number; md: number };
  index: number;
  extra?: React.ReactNode;
  big?: boolean;
};

/** Glossy gradient module tile: lacquer highlight, travelling shine, watermark icon, lift on hover. */
const GlossTile = ({
  title, metric, hint, icon: Icon, colors, onClick, span = { xs: 1, md: 3 }, index, extra, big,
}: TileProps) => (
  <Box
    role="link"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
    }}
    className="qs-animate-in"
    sx={{
      gridColumn: { xs: `span ${span.xs}`, md: `span ${span.md}` },
      position: 'relative',
      overflow: 'hidden',
      cursor: 'pointer',
      color: '#fff',
      minHeight: big ? 168 : 132,
      p: 2.25,
      borderRadius: '24px',
      background: `linear-gradient(135deg, ${colors[0]} 0%, ${colors[1]} 100%)`,
      boxShadow: `0 14px 28px ${colors[1]}55, inset 0 0 0 1px rgba(255,255,255,.35)`,
      animationDelay: `${0.05 * index}s`,
      transition: 'transform .25s cubic-bezier(.2,.8,.2,1), box-shadow .25s ease',
      outline: 'none',
      '&::before': {
        content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
        background:
          'linear-gradient(135deg, rgba(255,255,255,.38) 0%, rgba(255,255,255,.08) 42%, rgba(255,255,255,0) 43%, rgba(0,0,0,.12) 100%)',
      },
      '&::after': {
        content: '""', position: 'absolute', top: 0, bottom: 0, left: 0, width: '30%', pointerEvents: 'none',
        background: 'linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,.45), rgba(255,255,255,0))',
        transform: 'translateX(-140%) skewX(-22deg)',
      },
      '@media (prefers-reduced-motion: no-preference)': {
        '&::after': { animation: `${sheen} 7s ease-in-out ${index * 0.7}s infinite` },
        '&:hover': {
          transform: 'translateY(-6px) scale(1.02)',
          boxShadow: `0 22px 40px ${colors[1]}77, inset 0 0 0 1px rgba(255,255,255,.5)`,
        },
        '&:hover .qs-tile-watermark': { transform: 'rotate(-4deg) scale(1.12)' },
      },
      '&:active': { transform: 'scale(.97)' },
      '&:focus-visible': { boxShadow: `0 0 0 3px #fff, 0 0 0 6px ${colors[1]}` },
    }}
  >
    <Icon
      className="qs-tile-watermark"
      sx={{
        position: 'absolute', right: -14, bottom: -20, fontSize: big ? 150 : 120,
        color: 'rgba(255,255,255,.18)', transform: 'rotate(-12deg)',
        transition: 'transform .35s ease',
        '@media (prefers-reduced-motion: no-preference)': { animation: `${bob} 6s ease-in-out ${index * 0.4}s infinite` },
      }}
    />
    <Stack sx={{ position: 'relative', height: '100%' }} justifyContent="space-between" spacing={1.5}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box
          sx={{
            width: 42, height: 42, borderRadius: '14px', display: 'grid', placeItems: 'center',
            background: 'linear-gradient(135deg, rgba(255,255,255,.45), rgba(255,255,255,.12))',
            border: '1px solid rgba(255,255,255,.5)', backdropFilter: 'blur(6px)',
          }}
        >
          <Icon sx={{ fontSize: 22 }} />
        </Box>
        <ChevronRightRounded sx={{ opacity: 0.8 }} />
      </Stack>
      <Box>
        <Typography
          sx={{ fontWeight: 900, fontSize: big ? 34 : 24, lineHeight: 1.1, textShadow: '0 2px 8px rgba(0,0,0,.25)' }}
          noWrap
        >
          {metric}
        </Typography>
        <Typography sx={{ fontWeight: 800, fontSize: 15, mt: 0.25 }}>{title}</Typography>
        {hint && <Typography variant="caption" sx={{ opacity: 0.92, fontWeight: 600 }}>{hint}</Typography>}
        {extra}
      </Box>
    </Stack>
  </Box>
);

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */
const Dashboard = () => {
  const navigate = useNavigate();
  const { mode } = useThemeMode();
  const user = getStoredUser<{ fullName?: string }>();
  const quote = useMemo(() => QUOTES[new Date().getDate() % QUOTES.length], []);

  const [noteCount, setNoteCount] = useState(0);
  const [agenda, setAgenda] = useState<AgendaItem[]>([]);
  const [paymentCounts, setPaymentCounts] = useState({ received: 0, pending: 0 });
  const [payStats, setPayStats] = useState({ totalReceived: 0, totalPaid: 0, balance: 0 });
  const [groceryStats, setGroceryStats] = useState({ total: 0, toBuy: 0, bought: 0 });
  const [docTotal, setDocTotal] = useState(0);
  const [overview, setOverview] = useState<Awaited<ReturnType<typeof getReportsOverview>> | null>(null);
  const [statusOverview, setStatusOverview] = useState({ completed: 0, pending: 0, overdue: 0 });

  useEffect(() => {
    const now = new Date();
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    (async () => {
      try {
        const [notesRes, payments, pStats, gStats, dStats, ov] = await Promise.all([
          fetchNotes('', ''),
          listPayments({ month }),
          getPaymentStats(month),
          getGroceryStats(),
          getDocumentStats(),
          getReportsOverview(),
        ]);

        const notes: any[] = Array.isArray(notesRes.data) ? notesRes.data : notesRes.data?.items || [];
        setNoteCount(notes.length);
        setPayStats(pStats);
        setGroceryStats(gStats);
        setDocTotal(dStats.total);
        setOverview(ov);

        const received = payments.filter((p: PaymentItem) => p.status === 'completed').length;
        const pending = payments.filter((p: PaymentItem) => p.status === 'pending').length;
        setPaymentCounts({ received, pending });

        const overdue = payments.filter((p) => p.status === 'pending' && new Date(p.date) < now).length;
        setStatusOverview({ completed: received, pending: pending - overdue, overdue });

        const items: AgendaItem[] = [];
        notes.forEach((n) => {
          if (n.entryDate) items.push({ title: n.title || 'Untitled note', date: new Date(n.entryDate), kind: 'note' });
        });
        payments.forEach((p) => items.push({ title: p.title, date: new Date(p.date), kind: 'payment' }));

        setAgenda(items);
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
      }
    })();
  }, []);

  const today = agenda.filter((i) => isSameDay(i.date, new Date()));
  const upcoming = agenda
    .filter((i) => i.date.getTime() > Date.now() && !isSameDay(i.date, new Date()))
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .slice(0, 5);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good Morning';
    if (h < 17) return 'Good Afternoon';
    return 'Good Evening';
  })();

  const statusLegend = [
    { key: 'Completed', value: statusOverview.completed, color: CATEGORICAL[mode][0] },
    { key: 'Pending', value: statusOverview.pending, color: CATEGORICAL[mode][3] },
    { key: 'Overdue', value: statusOverview.overdue, color: CATEGORICAL[mode][7] },
  ];
  const donutData = statusLegend.filter((d) => d.value > 0).map((d) => ({ name: d.key, value: d.value }));

  const balanceAnim = useCountUp(payStats.balance);
  const toBuyAnim = useCountUp(groceryStats.toBuy);
  const notesAnim = useCountUp(noteCount);
  const docsAnim = useCountUp(docTotal);
  const dateLabel = new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
  const boughtPct = groceryStats.total ? Math.round((groceryStats.bought / groceryStats.total) * 100) : 0;
  const flow = payStats.totalReceived + payStats.totalPaid;
  const incomeShare = flow > 0 ? Math.round((payStats.totalReceived / flow) * 100) : 50;

  const kindIcon = (kind: AgendaItem['kind']) =>
    kind === 'note'
      ? { Icon: StickyNote2Rounded, color: '#6226A7' }
      : { Icon: PaymentsRounded, color: '#0277bd' };

  const quickActions = [
    { label: 'New note', to: '/notes/create' },
    { label: 'Add payment', to: '/payments' },
    { label: 'Add grocery', to: '/grocery' },
  ];

  return (
    <Box>
      {/* ===== Aurora hero ===== */}
      <Box
        className="qs-animate-in"
        sx={{
          position: 'relative', overflow: 'hidden', color: '#fff', mb: 3,
          p: { xs: 2.5, md: 4 }, borderRadius: '28px',
          background: 'linear-gradient(120deg, #2E0F5E 0%, #6226A7 32%, #B13FD6 62%, #F6A938 100%)',
          backgroundSize: '220% 220%',
          boxShadow: '0 24px 50px rgba(98,38,167,.35), inset 0 0 0 1px rgba(255,255,255,.25)',
          '@media (prefers-reduced-motion: no-preference)': { animation: 'qsGradientShift 12s ease infinite' },
        }}
      >
        <Box
          sx={{
            position: 'absolute', width: 320, height: 320, right: -60, top: -110, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,214,122,.65), transparent 65%)',
            '@media (prefers-reduced-motion: no-preference)': { animation: `${float1} 11s ease-in-out infinite` },
          }}
        />
        <Box
          sx={{
            position: 'absolute', width: 360, height: 360, left: -120, bottom: -190, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,111,216,.55), transparent 65%)',
            '@media (prefers-reduced-motion: no-preference)': { animation: `${float2} 13s ease-in-out infinite` },
          }}
        />
        <Box
          sx={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'linear-gradient(180deg, rgba(255,255,255,.22) 0%, rgba(255,255,255,0) 38%)',
          }}
        />

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          justifyContent="space-between"
          alignItems={{ md: 'flex-end' }}
          sx={{ position: 'relative' }}
        >
          <Box sx={{ maxWidth: 560 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', opacity: 0.9 }}>
              {dateLabel}
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 900, fontSize: { xs: 30, md: 42 }, lineHeight: 1.1, textShadow: '0 4px 18px rgba(0,0,0,.25)' }}
            >
              {greeting},<br />
              <Box
                component="span"
                sx={{ background: 'linear-gradient(90deg,#FFE29A,#FFFFFF)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
              >
                {user?.fullName?.split(' ')[0] || 'there'}
              </Box>{' '}
              👋
            </Typography>
            <Box
              sx={{
                mt: 1.5, display: 'inline-block', px: 2, py: 0.75, borderRadius: '999px',
                background: 'rgba(255,255,255,.18)', border: '1px solid rgba(255,255,255,.3)', backdropFilter: 'blur(8px)',
              }}
            >
              <Typography variant="body2">💡 {quote}</Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ mt: 2.5, flexWrap: 'wrap', rowGap: 1 }}>
              {quickActions.map((a) => (
                <Box
                  key={a.label}
                  component="button"
                  onClick={() => navigate(a.to)}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                    px: 1.75, py: 0.9, borderRadius: '999px', border: 0, fontWeight: 800, fontSize: 13,
                    color: '#4a1d86', background: 'linear-gradient(180deg,#fff,#EFE3FF)',
                    boxShadow: '0 6px 14px rgba(0,0,0,.2), inset 0 1px 0 #fff',
                    transition: 'transform .15s ease',
                    '&:hover': { transform: 'translateY(-2px)' },
                    '&:active': { transform: 'scale(.96)' },
                  }}
                >
                  <AddRounded sx={{ fontSize: 16 }} /> {a.label}
                </Box>
              ))}
            </Stack>
          </Box>

          <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 1.5, flex: { md: '0 1 520px' } }}>
            <HeroStat icon={PaymentsRounded} label="Balance (month)" value={fmtMoney(balanceAnim)} />
            <HeroStat icon={ShoppingCartRounded} label="To buy" value={String(Math.round(toBuyAnim))} />
            <HeroStat icon={StickyNote2Rounded} label="Notes" value={String(Math.round(notesAnim))} />
          </Stack>
        </Stack>
      </Box>

      {/* ===== Glossy bento ===== */}
      <Box
        sx={{
          display: 'grid', gap: 2, mb: 3,
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(12, 1fr)' },
        }}
      >
        <GlossTile
          index={0} big span={{ xs: 2, md: 6 }} title="Payments" icon={PaymentsRounded}
          colors={['#11998E', '#38EF7D']} onClick={() => navigate('/payments')}
          metric={fmtMoney(balanceAnim)}
          hint={`${paymentCounts.received} received · ${paymentCounts.pending} pending this month`}
          extra={
            <Box sx={{ mt: 1.25, height: 8, borderRadius: 4, bgcolor: 'rgba(0,0,0,.22)', overflow: 'hidden', maxWidth: 360 }}>
              <Box
                sx={{
                  width: `${incomeShare}%`, height: '100%', borderRadius: 4,
                  background: 'linear-gradient(90deg,#fff,#d7fff0)', transition: 'width 1s ease',
                }}
              />
            </Box>
          }
        />
        <GlossTile
          index={1} big span={{ xs: 1, md: 3 }} title="Grocery" icon={ShoppingCartRounded}
          colors={['#FF9800', '#E65100']} onClick={() => navigate('/grocery')}
          metric={`${Math.round(toBuyAnim)} to buy`} hint={`${boughtPct}% of ${groceryStats.total} bought`}
        />
        <GlossTile
          index={2} big span={{ xs: 1, md: 3 }} title="Notes" icon={StickyNote2Rounded}
          colors={['#6226A7', '#B06AB3']} onClick={() => navigate('/notes')}
          metric={String(Math.round(notesAnim))} hint="Ideas & reminders"
        />
        <GlossTile
          index={3} title="Documents" icon={FolderRounded} colors={['#CF1E52', '#FF6B8B']}
          onClick={() => navigate('/documents')} metric={String(Math.round(docsAnim))} hint="Files & folders"
        />
        <GlossTile
          index={4} title="Calendar" icon={CalendarMonthRounded} colors={['#2563EB', '#60A5FA']}
          onClick={() => navigate('/calendar')} metric={`${today.length} today`} hint={`${upcoming.length} upcoming`}
        />
        <GlossTile
          index={5} title="Reports" icon={BarChartRounded} colors={['#0F766E', '#2DD4BF']}
          onClick={() => navigate('/reports')} metric="Insights" hint="Trends & totals"
        />
        <GlossTile
          index={6} title="Chat" icon={ChatRounded} colors={['#00B4DB', '#0083B0']}
          onClick={() => navigate('/chat')} metric="Messages" hint="Talk in real time"
        />
        <GlossTile
          index={7} title="Tunes" icon={LibraryMusicRounded} colors={['#4776E6', '#8E54E9']}
          onClick={() => navigate('/tunes')} metric="Music" hint="Your playlists"
        />
        <GlossTile
          index={8} title="Videos" icon={OndemandVideoRounded} colors={['#EB3349', '#F45C43']}
          onClick={() => navigate('/videos')} metric="Watch" hint="Watch & learn"
        />
        <GlossTile
          index={9} title="PMA" icon={WorkRounded} colors={['#373B44', '#4286F4']}
          onClick={() => navigate('/pma')} metric="Projects" hint="Tasks & time"
        />
        <GlossTile
          index={10} title="Speed Test" icon={SpeedRounded} colors={['#1E3A8A', '#2563EB']}
          onClick={() => navigate('/internet-speed')} metric="Network" hint="Check your speed"
        />
      </Box>

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card className="qs-animate-in" sx={{ ...glassCardSx, p: 2.5, pt: 3, height: '100%', animationDelay: '.16s' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Today's Items</Typography>
              <IconButton size="small" onClick={() => navigate('/calendar')}><ChevronRightRounded fontSize="small" /></IconButton>
            </Stack>
            <Stack divider={<Divider />} spacing={1.25}>
              {today.length === 0 && <Typography variant="body2" color="text.secondary">Nothing due today.</Typography>}
              {today.map((item, i) => {
                const { Icon, color } = kindIcon(item.kind);
                return (
                  <Stack key={i} direction="row" alignItems="center" spacing={1.25}>
                    <Checkbox size="small" disabled sx={{ p: 0 }} />
                    <Box sx={{ width: 30, height: 30, borderRadius: '10px', display: 'grid', placeItems: 'center', bgcolor: `${color}1F`, color, flexShrink: 0 }}>
                      <Icon sx={{ fontSize: 16 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{item.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.kind}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {item.date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card className="qs-animate-in" sx={{ ...glassCardSx, p: 2.5, pt: 3, height: '100%', animationDelay: '.24s' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Upcoming</Typography>
              <IconButton size="small" onClick={() => navigate('/calendar')}><ChevronRightRounded fontSize="small" /></IconButton>
            </Stack>
            <Stack divider={<Divider />} spacing={1.25}>
              {upcoming.length === 0 && <Typography variant="body2" color="text.secondary">Nothing coming up.</Typography>}
              {upcoming.map((item, i) => {
                const { Icon, color } = kindIcon(item.kind);
                return (
                  <Stack key={i} direction="row" alignItems="center" spacing={1.25}>
                    <Box sx={{ width: 30, height: 30, borderRadius: '10px', display: 'grid', placeItems: 'center', bgcolor: `${color}1F`, color, flexShrink: 0 }}>
                      <Icon sx={{ fontSize: 16 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{item.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.kind}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {item.date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card className="qs-animate-in" sx={{ ...glassCardSx, p: 2.5, pt: 3, height: '100%', animationDelay: '.32s' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Status Overview</Typography>
            {donutData.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No payment activity yet.</Typography>
            ) : (
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ width: 120, flexShrink: 0 }}>
                  <DonutChart data={donutData} height={120} />
                </Box>
                <Stack spacing={1} sx={{ flex: 1 }}>
                  {statusLegend.map((s) => (
                    <Stack key={s.key} direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color }} />
                        <Typography variant="body2" color="text.secondary">{s.key}</Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.value}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            )}
          </Card>
        </Grid>
      </Grid>

      <Card className="qs-animate-in" sx={{ ...glassCardSx, p: 2.5, pt: 3, animationDelay: '.4s' }}>
        <PageHeader title="Summary (This Month)" />
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard icon={StickyNote2Rounded} label="Notes" value={overview?.notes.count ?? 0} deltaPct={overview?.notes.deltaPct ?? undefined} color="#6226A7" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard icon={PaymentsRounded} label="Payments" value={overview?.payments.count ?? 0} deltaPct={overview?.payments.deltaPct ?? undefined} color="#0277bd" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard icon={ShoppingCartRounded} label="Grocery" value={overview?.groceryItems.count ?? 0} deltaPct={overview?.groceryItems.deltaPct ?? undefined} color="#F6A938" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard icon={FolderRounded} label="Documents" value={overview?.documents.count ?? 0} deltaPct={overview?.documents.deltaPct ?? undefined} color="#CF1E52" />
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
};

export default Dashboard;
